import { useEffect, useState } from "react";
import {
  Badge, Box, Button, Collapse, Flex, FormControl, FormLabel, Input, Select, SimpleGrid, Spinner, Switch, Text, Textarea, Tooltip, useToast,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import {
  FiscalEntryDefaults, FiscalIcmsProfile, FiscalIssuer, FiscalSaleDefaults, FiscalShipmentDefaults, getFiscalIssuers, saveFiscalIssuer,
} from "../../../services/fiscalService";
import FiscalIssuerAdmin from "./FiscalIssuerAdmin";
import FiscalNumberVoids from "./FiscalNumberVoids";

const UNIT_NAMES: Record<number, string> = { 1: "CVF Logística", 2: "CVF Distribuidora", 3: "CVF Rondônia", 4: "W Trading" };

// Limites iguais aos do servidor (FiscalIssuerMap)
const FIELDS: [keyof FiscalIssuer, string, number][] = [
  ["name", "Razão social", 60],
  ["tradeName", "Nome fantasia", 60],
  ["cnpj", "CNPJ", 18],
  ["stateRegistration", "Inscrição estadual", 20],
  ["street", "Logradouro", 60],
  ["number", "Número", 60],
  ["complement", "Complemento", 60],
  ["district", "Bairro", 60],
  ["city", "Município", 60],
  ["cityCode", "Código IBGE do município", 7],
  ["state", "UF", 2],
  ["zipCode", "CEP", 9],
  ["phone", "Telefone", 20],
];

const defaultEntry = (): FiscalEntryDefaults => ({
  natureOperation: "Importação", cfop: "3102", onBehalf: false, iiInProductValue: true, origin: 1, icmsCst: "51", icmsBaseMode: 3,
  icmsDeferralPercent: 100, benefitCode: "SC830015", ipiCst: "49", ipiLegalCode: "999", pisCofinsCst: "56", ibsCbsCst: "000",
  ibsCbsClassification: "000001", ibsStateRate: 0.1, ibsCityRate: 0, cbsRate: 0.9, freightMode: 0, unit: "UN", volumeKind: "PEÇAS",
  customsLocation: "0927800 - ITAJAI", customsState: "SC", fiscoTextTemplate: "IMPORTACAO - {processo} {refCliente} DI/DUIMP {duimp}",
});

const blank = (): FiscalIssuer => ({
  id: null, unitType: null, cnpj: "", name: "", taxRegime: 3, environment: "homologacao", enabled: true, entryDefaults: defaultEntry(),
});

// Padrões da NF de entrada: [campo, rótulo, tipo, tamanho máx.]
const ENTRY_FIELDS: [keyof FiscalEntryDefaults, string, "text" | "number", number][] = [
  ["natureOperation", "Natureza da operação", "text", 60],
  ["cfop", "CFOP", "text", 4],
  ["icmsCst", "CST ICMS", "text", 3],
  ["icmsDeferralPercent", "% diferimento ICMS", "number", 6],
  ["benefitCode", "cBenef", "text", 10],
  ["ipiCst", "CST IPI", "text", 3],
  ["pisCofinsCst", "CST PIS/COFINS", "text", 3],
  ["ibsCbsCst", "CST IBS/CBS", "text", 3],
  ["ibsCbsClassification", "cClassTrib IBS/CBS", "text", 6],
  ["ibsStateRate", "% IBS UF", "number", 6],
  ["ibsCityRate", "% IBS município", "number", 6],
  ["cbsRate", "% CBS", "number", 6],
  ["unit", "Unidade", "text", 6],
  ["volumeKind", "Espécie do volume", "text", 60],
  ["customsLocation", "Local de desembaraço padrão", "text", 60],
  ["customsState", "UF do desembaraço", "text", 2],
];

// Padrões da remessa por conta e ordem e da filhote: [campo, rótulo, tamanho máx.]
const SHIPMENT_FIELDS: [keyof FiscalShipmentDefaults, string, number][] = [
  ["natureOperation", "Natureza da operação (remessa)", 60],
  ["cfop", "CFOP (mesma UF)", 4],
  ["interstateCfop", "CFOP (outra UF)", 4],
  ["pisCofinsCst", "CST PIS/COFINS", 3],
  ["ipiCst", "CST IPI", 3],
  ["ibsCbsCst", "CST IBS/CBS", 3],
  ["ibsCbsClassification", "cClassTrib IBS/CBS", 6],
  ["childNatureOperation", "Natureza da operação (filhote)", 60],
  ["childIcmsCst", "CST ICMS (filhote)", 3],
  ["childIpiCst", "CST IPI (filhote)", 3],
  ["childBenefitCode", "cBenef (filhote)", 10],
];

// Padrões da NF de venda: [campo, rótulo, tamanho máx.]
const SALE_FIELDS: [keyof FiscalSaleDefaults, string, number][] = [
  ["natureOperation", "Natureza (venda própria, sem parametrização)", 60],
  ["onOrderNatureOperation", "Natureza (encomenda, sem parametrização)", 60],
  ["ipiCst", "CST IPI", 3],
  ["ipiLegalCode", "Enquadramento IPI", 3],
  ["pisCofinsCst", "CST PIS/COFINS", 3],
  ["ibsCbsCst", "CST IBS/CBS", 3],
  ["ibsCbsClassification", "cClassTrib IBS/CBS", 6],
  ["paymentMethod", "Forma de pagamento (tPag)", 2],
];

// Colunas do perfil de ICMS: [campo, rótulo, tipo, tamanho máx.]
const PROFILE_FIELDS: [keyof FiscalIcmsProfile, string, "text" | "number", number][] = [
  ["name", "Nome", "text", 40],
  ["cst", "CST", "text", 3],
  ["rate", "Alíquota %", "number", 6],
  ["baseReduction", "Redução base %", "number", 8],
  ["benefitCode", "cBenef", "text", 10],
  ["presumedCreditCode", "Créd. presumido", "text", 10],
  ["presumedCreditRate", "% créd. presumido", "number", 6],
];

/** Empresas que emitem NF-e pela Focus NFe. Token da Focus: salvo na tela (criptografado) ou variável do servidor (FocusNfe__Tokens__{cnpj}__{ambiente}). */
const FiscalIssuers = () => {
  const toast = useToast();
  const [issuers, setIssuers] = useState<FiscalIssuer[]>([]);
  const [saved, setSaved] = useState<FiscalIssuer[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [openEntry, setOpenEntry] = useState<number | null>(null);
  const [openShipment, setOpenShipment] = useState<number | null>(null);
  const [openSale, setOpenSale] = useState<number | null>(null);
  const [openVoids, setOpenVoids] = useState<number | null>(null);

  useEffect(() => {
    getFiscalIssuers()
      .then((list) => { setIssuers(list); setSaved(list); })
      .catch(() => toast({ status: "error", title: "Não foi possível carregar os emitentes" }))
      .finally(() => setLoading(false));
  }, []);

  const patch = (index: number, field: keyof FiscalIssuer, value: any) =>
    setIssuers((list) => list.map((u, i) => (i === index ? { ...u, [field]: value } : u)));
  const patchEntry = (index: number, field: keyof FiscalEntryDefaults, value: any) =>
    setIssuers((list) => list.map((u, i) => (i === index ? { ...u, entryDefaults: { ...(u.entryDefaults ?? defaultEntry()), [field]: value } } : u)));

  const patchShipment = (index: number, change: (d: FiscalShipmentDefaults) => FiscalShipmentDefaults) =>
    setIssuers((list) => list.map((u, i) => (i === index && u.shipmentDefaults ? { ...u, shipmentDefaults: change(u.shipmentDefaults) } : u)));
  const patchSale = (index: number, field: keyof FiscalSaleDefaults, value: any) =>
    setIssuers((list) => list.map((u, i) => (i === index && u.saleDefaults ? { ...u, saleDefaults: { ...u.saleDefaults, [field]: value } } : u)));
  const patchProfile = (index: number, row: number, field: keyof FiscalIcmsProfile, value: any) =>
    patchShipment(index, (d) => {
      const old = d.icmsProfiles[row];
      const profiles = d.icmsProfiles.map((p, k) => (k === row ? { ...p, [field]: value } : p));
      // Renomear o perfil padrão leva o padrão junto
      return { ...d, icmsProfiles: profiles, defaultProfile: field === "name" && d.defaultProfile === old.name ? value : d.defaultProfile };
    });

  /** Token/ambiente salvos pelo painel admin: grava no salvo e só esses campos no formulário (mantém edição pendente). */
  const adminSaved = (index: number, result: FiscalIssuer) => {
    const pick = ({ environment, blocker, blockerCode, homologationTokenSource, productionTokenSource }: FiscalIssuer) =>
      ({ environment, blocker, blockerCode, homologationTokenSource, productionTokenSource });
    setSaved((list) => list.map((u, i) => (i === index ? { ...u, ...pick(result) } : u)));
    setIssuers((list) => list.map((u, i) => (i === index ? { ...u, ...pick(result) } : u)));
  };

  const save = async (index: number) => {
    setSaving(index);
    try {
      // Padrões de remessa só vão para emitente de conta e ordem (senão gravaria os padrões do código em todo emitente)
      const issuer = issuers[index];
      const names = (issuer.entryDefaults?.onBehalf ? issuer.shipmentDefaults?.icmsProfiles ?? [] : []).map((p) => (p.name ?? "").trim().toUpperCase());
      if (names.some((n) => !n) || new Set(names).size !== names.length) {
        toast({ status: "error", title: "Perfis de ICMS precisam de nome, sem repetir" });
        return;
      }
      // Nome do perfil vai sem espaço nas pontas (o servidor procura o perfil pelo nome limpo)
      const sd = issuer.shipmentDefaults;
      const shipmentDefaults = issuer.entryDefaults?.onBehalf && sd
        ? { ...sd, defaultProfile: sd.defaultProfile.trim(), icmsProfiles: sd.icmsProfiles.map((p) => ({ ...p, name: p.name.trim() })) }
        : null;
      // Padrões de venda só para quem vende (emitente que não é de conta e ordem)
      const saleDefaults = !issuer.entryDefaults?.onBehalf ? issuer.saleDefaults ?? null : null;
      const result = await saveFiscalIssuer({ ...issuer, shipmentDefaults, saleDefaults });
      setIssuers((list) => list.map((u, i) => (i === index ? result : u)));
      setSaved((list) => {
        const next = [...list];
        next[index] = result;
        return next;
      });
      toast({ status: "success", title: `${result.name} salvo` });
    } catch (e: any) {
      toast({ status: "error", title: "Não foi possível salvar", description: e?.message, duration: 8000, isClosable: true });
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <Flex justify="center" py="32px"><Spinner /></Flex>;

  return (
    <Box w="100%">
      <Flex justify="space-between" align="center" wrap="wrap" gap="8px">
        <Box>
          <Text fontSize="20px" fontWeight="700" color={crm.text}>Notas fiscais · emitentes</Text>
          <Text color={crm.textMuted} fontSize="sm" mt="4px">
            Empresas que emitem NF-e pelo sistema (Focus NFe). Série e numeração são configuradas no painel da Focus (próximo número depois do último emitido pelo ERP).
          </Text>
        </Box>
        <Button size="sm" variant="outline" onClick={() => { setIssuers((l) => [...l, blank()]); setSaved((l) => [...l, blank()]); }}>
          Novo emitente
        </Button>
      </Flex>

      {issuers.map((issuer, index) => {
        const dirty = JSON.stringify(issuer) !== JSON.stringify(saved[index]);
        return (
          <Box key={issuer.id ?? `new-${index}`} border={`1px solid ${crm.border}`} borderRadius="12px" p="16px" mt="14px">
            <Flex justify="space-between" align="center" mb="10px" wrap="wrap" gap="8px">
              <Flex align="center" gap="8px" wrap="wrap">
                <Text fontWeight="700">{issuer.name || "Novo emitente"}</Text>
                <Badge colorScheme={issuer.environment === "producao" ? "red" : "purple"}>
                  {issuer.environment === "producao" ? "Produção" : "Homologação"}
                </Badge>
                {!issuer.enabled && <Badge>Desativado</Badge>}
                {issuer.id && (issuer.blocker
                  ? (
                    <Tooltip label={issuer.blocker} hasArrow>
                      <Badge colorScheme="orange" cursor="help">
                        {issuer.blockerCode === "token_unreadable" ? "Token ilegível" : issuer.blockerCode === "production_locked" ? "Produção bloqueada" : "Sem token"}
                      </Badge>
                    </Tooltip>
                  )
                  : <Badge colorScheme="green">Pronto para emitir</Badge>)}
              </Flex>
              <Button size="sm" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isDisabled={!dirty}
                isLoading={saving === index} onClick={() => save(index)}>
                Salvar
              </Button>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10px">
              {FIELDS.map(([field, label, max]) => (
                <FormControl key={field}>
                  <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">{label}</FormLabel>
                  <Input size="sm" maxLength={max} value={(issuer[field] as string) ?? ""} onChange={(e) => patch(index, field, e.target.value)} />
                </FormControl>
              ))}
              <FormControl>
                <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Unidade</FormLabel>
                <Select size="sm" value={issuer.unitType ?? ""} onChange={(e) => patch(index, "unitType", e.target.value ? Number(e.target.value) : null)}>
                  <option value="">—</option>
                  {Object.entries(UNIT_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Regime tributário</FormLabel>
                <Select size="sm" value={issuer.taxRegime} onChange={(e) => patch(index, "taxRegime", Number(e.target.value))}>
                  <option value={1}>Simples Nacional</option>
                  <option value={2}>Simples Nacional (excesso de sublimite)</option>
                  <option value={3}>Regime normal</option>
                </Select>
              </FormControl>
              <FormControl display="flex" alignItems="center" gap="8px" mt={{ base: 0, md: "22px" }}>
                <Switch colorScheme="green" isChecked={issuer.enabled} onChange={(e) => patch(index, "enabled", e.target.checked)} />
                <FormLabel fontSize="13px" m="0">Ativo</FormLabel>
              </FormControl>
            </SimpleGrid>

            {issuer.id && saved[index]?.id && (
              <FiscalIssuerAdmin issuer={saved[index]} dirty={dirty} onSaved={(result) => adminSaved(index, result)} />
            )}

            <Button variant="link" size="sm" mt="12px" onClick={() => setOpenEntry(openEntry === index ? null : index)}>
              {openEntry === index ? "Ocultar" : "Mostrar"} padrões da NF de entrada (importação)
            </Button>
            <Collapse in={openEntry === index} animateOpacity>
              <Box mt="10px" p="12px" bg={crm.subtle} borderRadius="10px">
                <Text fontSize="12px" color={crm.textMuted} mb="10px">
                  Usados ao gerar a NF de entrada a partir da DUIMP. Valores iniciais copiados das notas que o ERP emitia; confira com o contador.
                </Text>
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing="10px">
                  {ENTRY_FIELDS.map(([field, label, type, max]) => (
                    <FormControl key={field}>
                      <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">{label}</FormLabel>
                      <Input size="sm" bg="white" maxLength={max} type={type === "number" ? "number" : "text"} step="0.01"
                        value={(issuer.entryDefaults?.[field] as any) ?? ""}
                        onChange={(e) => patchEntry(index, field, type === "number" ? (e.target.value === "" ? 0 : Number(e.target.value)) : e.target.value)} />
                    </FormControl>
                  ))}
                  <FormControl>
                    <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Frete</FormLabel>
                    <Select size="sm" bg="white" value={issuer.entryDefaults?.freightMode ?? 0} onChange={(e) => patchEntry(index, "freightMode", Number(e.target.value))}>
                      <option value={0}>0 · por conta do remetente</option>
                      <option value={1}>1 · por conta do destinatário</option>
                      <option value={2}>2 · terceiros</option>
                      <option value={9}>9 · sem frete</option>
                    </Select>
                  </FormControl>
                  <FormControl display="flex" alignItems="center" gap="8px" mt={{ base: 0, md: "22px" }}>
                    <Switch isChecked={!!issuer.entryDefaults?.onBehalf} onChange={(e) => patchEntry(index, "onBehalf", e.target.checked)} />
                    <FormLabel fontSize="13px" m="0">Conta e ordem</FormLabel>
                  </FormControl>
                  <FormControl display="flex" alignItems="center" gap="8px" mt={{ base: 0, md: "22px" }}>
                    <Switch isChecked={!!issuer.entryDefaults?.iiInProductValue} onChange={(e) => patchEntry(index, "iiInProductValue", e.target.checked)} />
                    <FormLabel fontSize="13px" m="0">II somado no valor do produto</FormLabel>
                  </FormControl>
                </SimpleGrid>
                <FormControl mt="10px">
                  <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">
                    Texto ao fisco · marcadores: {"{processo} {cliente} {refCliente} {duimp} {despesas} {despesasDetalhe} {pis} {cofins}"}
                  </FormLabel>
                  <Textarea size="sm" bg="white" rows={3} maxLength={2000} value={issuer.entryDefaults?.fiscoTextTemplate ?? ""}
                    onChange={(e) => patchEntry(index, "fiscoTextTemplate", e.target.value)} />
                </FormControl>
              </Box>
            </Collapse>

            {!issuer.entryDefaults?.onBehalf && issuer.saleDefaults && (
              <>
                <Button variant="link" size="sm" mt="12px" ml={{ md: "16px" }} onClick={() => setOpenSale(openSale === index ? null : index)}>
                  {openSale === index ? "Ocultar" : "Mostrar"} padrões da NF de venda
                </Button>
                <Collapse in={openSale === index} animateOpacity>
                  <Box mt="10px" p="12px" bg={crm.subtle} borderRadius="10px">
                    <Text fontSize="12px" color={crm.textMuted} mb="10px">
                      CFOP, CST do ICMS e alíquotas vêm da parametrização fiscal do cliente. Aqui ficam os CSTs fixos e o que a tela sugere (valores da NF 7934 do ERP).
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing="10px">
                      {SALE_FIELDS.map(([field, label, max]) => (
                        <FormControl key={field}>
                          <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">{label}</FormLabel>
                          <Input size="sm" bg="white" maxLength={max} value={(issuer.saleDefaults?.[field] as any) ?? ""}
                            onChange={(e) => patchSale(index, field, e.target.value)} />
                        </FormControl>
                      ))}
                      <FormControl>
                        <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Origem (venda própria)</FormLabel>
                        <Input size="sm" bg="white" type="number" min={0} max={8} value={issuer.saleDefaults.origin}
                          onChange={(e) => patchSale(index, "origin", e.target.value === "" ? 0 : Number(e.target.value))} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Frete sugerido</FormLabel>
                        <Select size="sm" bg="white" value={issuer.saleDefaults.freightMode} onChange={(e) => patchSale(index, "freightMode", Number(e.target.value))}>
                          <option value={0}>0 · por conta do remetente</option>
                          <option value={1}>1 · por conta do destinatário</option>
                          <option value={2}>2 · terceiros</option>
                          <option value={9}>9 · sem frete</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Presença do comprador</FormLabel>
                        <Select size="sm" bg="white" value={issuer.saleDefaults.buyerPresence} onChange={(e) => patchSale(index, "buyerPresence", Number(e.target.value))}>
                          <option value={0}>0 · não se aplica (como o ERP)</option>
                          <option value={1}>1 · presencial</option>
                          <option value={2}>2 · internet</option>
                          <option value={3}>3 · teleatendimento</option>
                          <option value={9}>9 · não presencial, outros</option>
                        </Select>
                      </FormControl>
                    </SimpleGrid>
                    <FormControl mt="10px">
                      <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">
                        Texto ao fisco quando a parametrização não tem · marcadores: {"{processo} {cliente} {refCliente} {duimp} {conteiner} {pedido}"}
                      </FormLabel>
                      <Textarea size="sm" bg="white" rows={2} maxLength={2000} value={issuer.saleDefaults.fiscoTextTemplate ?? ""}
                        onChange={(e) => patchSale(index, "fiscoTextTemplate", e.target.value)} />
                    </FormControl>
                  </Box>
                </Collapse>
              </>
            )}
            {issuer.entryDefaults?.onBehalf && !issuer.shipmentDefaults && (
              <Text fontSize="13px" color={crm.textMuted} mt="10px">
                Padrões da remessa conta e ordem: salve o emitente para editar (até lá valem os padrões do sistema).
              </Text>
            )}
            {issuer.entryDefaults?.onBehalf && issuer.shipmentDefaults && (
              <ShipmentDefaultsEditor
                value={issuer.shipmentDefaults}
                open={openShipment === index}
                onToggle={() => setOpenShipment(openShipment === index ? null : index)}
                onChange={(change) => patchShipment(index, change)}
                onProfile={(row, field, value) => patchProfile(index, row, field, value)}
              />
            )}
            {issuer.id && (
              <>
                <Button variant="link" size="sm" mt="12px" ml={{ md: "16px" }} onClick={() => setOpenVoids(openVoids === index ? null : index)}>
                  {openVoids === index ? "Ocultar" : "Mostrar"} inutilização de numeração
                </Button>
                {/* Ambiente salvo (o que a Focus usa), não o que está sendo editado */}
                {openVoids === index && <FiscalNumberVoids issuerId={issuer.id} environment={saved[index]?.environment ?? issuer.environment} />}
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default FiscalIssuers;

/** Padrões da remessa por conta e ordem / filhote (só emitente de conta e ordem). */
const ShipmentDefaultsEditor = ({ value: sd, open, onToggle, onChange, onProfile }: {
  value: FiscalShipmentDefaults;
  open: boolean;
  onToggle: () => void;
  onChange: (change: (d: FiscalShipmentDefaults) => FiscalShipmentDefaults) => void;
  onProfile: (row: number, field: keyof FiscalIcmsProfile, value: any) => void;
}) => (
  <>
    <Button variant="link" size="sm" mt="12px" ml={{ md: "16px" }} onClick={onToggle}>
      {open ? "Ocultar" : "Mostrar"} padrões da remessa por conta e ordem / filhote
    </Button>
    <Collapse in={open} animateOpacity>
      <Box mt="10px" p="12px" bg={crm.subtle} borderRadius="10px">
        <Text fontSize="12px" color={crm.textMuted} mb="10px">
          Usados ao gerar a remessa a partir da NF de entrada autorizada e as filhotes a partir da remessa. Valores iniciais copiados das notas que o ERP emitia; confira com o contador.
        </Text>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing="10px">
          {SHIPMENT_FIELDS.map(([field, label, max]) => (
            <FormControl key={field}>
              <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">{label}</FormLabel>
              <Input size="sm" bg="white" maxLength={max} value={(sd[field] as string) ?? ""}
                onChange={(e) => onChange((d) => ({ ...d, [field]: e.target.value }))} />
            </FormControl>
          ))}
          <FormControl>
            <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Frete</FormLabel>
            <Select size="sm" bg="white" value={sd.freightMode} onChange={(e) => onChange((d) => ({ ...d, freightMode: Number(e.target.value) }))}>
              <option value={0}>0 · por conta do remetente</option>
              <option value={1}>1 · por conta do destinatário</option>
              <option value={2}>2 · terceiros</option>
              <option value={9}>9 · sem frete</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">Perfil de ICMS padrão</FormLabel>
            <Select size="sm" bg="white" value={sd.defaultProfile} onChange={(e) => onChange((d) => ({ ...d, defaultProfile: e.target.value }))}>
              {sd.icmsProfiles.map((p, k) => <option key={k} value={p.name}>{p.name}</option>)}
            </Select>
          </FormControl>
        </SimpleGrid>

        <Text fontSize="12px" fontWeight="700" mt="14px" mb="6px">
          Perfis de ICMS · valor da remessa = custo da entrada ÷ (1 − alíquota × (1 − redução))
        </Text>
        {sd.icmsProfiles.map((p, row) => (
          <Flex key={row} gap="6px" mb="6px" wrap="wrap" align="flex-end">
            {PROFILE_FIELDS.map(([field, label, type, max]) => (
              <FormControl key={field} w={field === "name" ? "180px" : "110px"}>
                {row === 0 && <FormLabel fontSize="11px" color={crm.textMuted} mb="2px">{label}</FormLabel>}
                <Input size="xs" bg="white" maxLength={max} type={type === "number" ? "number" : "text"} step="0.0001"
                  value={(p[field] as any) ?? ""}
                  onChange={(e) => onProfile(row, field, type === "number" ? (e.target.value === "" ? 0 : Number(e.target.value)) : e.target.value)} />
              </FormControl>
            ))}
            <Button size="xs" variant="ghost" colorScheme="red" isDisabled={sd.icmsProfiles.length === 1}
              onClick={() => onChange((d) => {
                const icmsProfiles = d.icmsProfiles.filter((_, k) => k !== row);
                // Removeu o padrão: o primeiro que sobrou vira padrão (senão o servidor recusa)
                const defaultProfile = d.defaultProfile === d.icmsProfiles[row].name ? icmsProfiles[0].name : d.defaultProfile;
                return { ...d, icmsProfiles, defaultProfile };
              })}>
              Remover
            </Button>
          </Flex>
        ))}
        <Button size="xs" variant="outline" bg="white" onClick={() => onChange((d) => ({
          ...d, icmsProfiles: [...d.icmsProfiles, { name: "", cst: "51", rate: 0, baseReduction: 0, presumedCreditRate: 0 }],
        }))}>
          Adicionar perfil
        </Button>

        <FormControl mt="12px">
          <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">
            Texto ao fisco da remessa · marcadores: {"{processo} {cliente} {refCliente} {duimp} {pis} {cofins} {despesas}"}
          </FormLabel>
          <Textarea size="sm" bg="white" rows={2} maxLength={2000} value={sd.fiscoTextTemplate}
            onChange={(e) => onChange((d) => ({ ...d, fiscoTextTemplate: e.target.value }))} />
        </FormControl>
        <FormControl mt="10px">
          <FormLabel fontSize="12px" color={crm.textMuted} mb="4px">
            Texto ao fisco da filhote · marcadores: {"{numeroMae} {serieMae} {dataMae} {chaveMae} {duimp} {conteiner} {processo}"}
          </FormLabel>
          <Textarea size="sm" bg="white" rows={3} maxLength={2000} value={sd.childFiscoTextTemplate}
            onChange={(e) => onChange((d) => ({ ...d, childFiscoTextTemplate: e.target.value }))} />
        </FormControl>
      </Box>
    </Collapse>
  </>
);
